"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";

type LoginFormProps = {
  locale: "en" | "ar";
  labels: { tenant: string; email: string; password: string; action: string; progress: string; error: string };
};

export function LoginForm({ locale, labels }: LoginFormProps) {
  const router = useRouter();
  const [error, setError] = useState(false);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(false);
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenant: form.get("tenant"), email: form.get("email"), password: form.get("password") }),
      });
      if (!response.ok) throw new Error("AUTHENTICATION_FAILED");
      const result = (await response.json()) as { csrfToken: string };
      sessionStorage.setItem("pb_csrf", result.csrfToken);
      router.replace(`/${locale}/workspace`);
      router.refresh();
    } catch {
      setError(true);
      setPending(false);
    }
  }

  return (
    <form className="auth-form" action="/api/auth/login" method="post" onSubmit={submit} noValidate>
      <label htmlFor="tenant">{labels.tenant}</label>
      <input id="tenant" name="tenant" autoComplete="organization" required minLength={3} />
      <label htmlFor="email">{labels.email}</label>
      <input id="email" name="email" type="email" autoComplete="username" required />
      <label htmlFor="password">{labels.password}</label>
      <input id="password" name="password" type="password" autoComplete="current-password" required minLength={12} />
      {error ? <p className="form-error" role="alert">{labels.error}</p> : null}
      <button type="submit" disabled={pending} aria-disabled={pending}>{pending ? labels.progress : labels.action}</button>
    </form>
  );
}
